import React, { useEffect, useReducer, createContext, useState } from 'react';
import { AES, enc } from 'crypto-js';
import { DATA_SALT, KEY_ACCESS_TOKEN, KEY_REFRESH_TOKEN, KEY_USER_DETAILS, TABLET_BREAKPOINT } from '../Utils/Constants/constant';
import KenLoader from '../Components/KenLoader';
import mixpanel from 'mixpanel-browser';
import KenDialog from '../Components/KenDialog';
import { Box, Stack, Typography } from '@mui/material';
import KenButton from '../Components/KenButton';
import { refreshToken } from '../Utils/apiServices';
import { makeStyles } from '@mui/styles';

export const initialState = {
  isAuthenticated: false,
  user: null,
  search: null,
  loading: false,
};

const useStyles = makeStyles((theme) => ({
  thankyouText: {
    fontSize: '1.3rem',
    fontWeight: '600',
    fontFamily: "'Nunito', sans-serif",
    color: '#373A45',
    '@media (max-width: 1440px)': {
      fontSize: '1rem',
    },
    [theme.breakpoints.down(TABLET_BREAKPOINT)]: {
      fontSize: '0.9rem',
    },
  },
  subTextModal: {
    fontSize: '1.1rem',
    fontWeight: '500',
    fontFamily: "'Nunito', sans-serif",
    color: '#373A45',
    '@media (max-width: 1440px)': {
      fontSize: '0.8rem',
    },
    [theme.breakpoints.down(TABLET_BREAKPOINT)]: {
      fontSize: '0.75rem',
    },
  },
  payBtn: {
    display: 'flex',
    gap: '0.5rem',
    width: 'max-content',
    minWidth: '7rem',
    height: '2.5rem',
    background: '#47712B !important',
    color: 'white !important',
    'border-radius': '0.5rem !important',
    'font-style': 'normal',
    'font-weight': '500 !important',
    'font-size': '1rem !important',
    'line-height': '100% !important',
    'text-align': 'center',
    '@media (max-width: 1440px)': {
      height: '2.25rem',
      'font-size': '0.9rem !important',
    },
    [theme.breakpoints.down(TABLET_BREAKPOINT)]: {
      'font-size': '0.75rem !important',
      width: '100%',
    },
  },
  goBack: {
    display: 'flex',
    padding: '0.5rem',
    gap: '0.5rem',
    height: '2.5rem',
    width: 'max-content',
    minWidth: '7rem',
    border: 'none !important',
    color: '#373A45 !important',
    border: '1px solid #373A45 !important',
    borderRadius: '8px !important',
    background: 'none !important',
    'font-style': 'normal',
    'font-weight': '500 !important',
    'font-size': '1rem !important',
    'line-height': '100% !important',
    'text-align': 'center',
    '@media (max-width: 1440px)': {
      height: '2.25rem',
      'font-size': '0.75rem !important',
    },
    [theme.breakpoints.down(TABLET_BREAKPOINT)]: {
      'font-size': '0.9rem !important',
      width: '100%',
    },
  },
}));

const reducer = (state, action) => {
  switch (action.type) {
    case 'LOGIN':
      const existingAccesstoken = sessionStorage.getItem(KEY_ACCESS_TOKEN);
      const existingRefreshtoken = sessionStorage.getItem(KEY_REFRESH_TOKEN);

      sessionStorage.setItem(KEY_ACCESS_TOKEN, action.payload?.accessToken?.token || existingAccesstoken);
      sessionStorage.setItem(KEY_REFRESH_TOKEN, action.payload?.refreshToken?.token || existingRefreshtoken);

      const userPayload = action.payload || { user: null };
      const data = AES.encrypt(JSON.stringify(userPayload), DATA_SALT).toString();
      sessionStorage.setItem(KEY_USER_DETAILS, data);
      return {
        ...state,
        loading: action.payload?.loading || state.loading,
        isAuthenticated: userPayload.access_token && userPayload.access_token === null ? false : true,
        user: userPayload.user || null,
      };
    case 'LOGOUT':
      sessionStorage.clear();
      mixpanel.reset();
      return {
        ...state,
        loading: true,
        isAuthenticated: false,
        user: null,
      };
    default:
      return state;
  }
};

export const AuthContext = createContext({ state: initialState, dispatch: () => {} });

export const AuthContextProvider = (props) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [openRequest, setOpenRequest] = useState(false);
  const classes = useStyles();

  const fetchUserData = (accessToken, refreshToken) => {
    const user = sessionStorage.getItem(KEY_USER_DETAILS);
    if (user) {
      const parseData = AES.decrypt(user, DATA_SALT).toString(enc.Utf8);
      const parsedUser = JSON.parse(parseData);
      const accessTokenCurrent = sessionStorage.getItem(KEY_ACCESS_TOKEN);
      const refreshTokenCurrent = sessionStorage.getItem(KEY_REFRESH_TOKEN);
      if (!parsedUser?.user) {
        dispatch({
          type: 'LOGOUT',
        });
        return;
      }
      dispatch({
        type: 'LOGIN',
        payload: {
          loading: true,
          user: parsedUser.user,
          accessToken: accessToken || accessTokenCurrent,
          refreshToken: refreshToken || refreshTokenCurrent,
        },
      });
    } else {
      dispatch({
        type: 'LOGOUT',
      });
    }
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  useEffect(() => {
    const session = sessionStorage.getItem('session');
    if (session) {
      setOpenRequest(true);
    }
  }, []);

  const handleOpenRequest = () => {
    setOpenRequest(!openRequest);
  };

  const handleLogout = () => {
    handleOpenRequest();
    sessionStorage.clear();
    window.location.reload();
  };

  const handleRefresh = async () => {
    sessionStorage.removeItem('session');
    const getToken = await refreshToken(sessionStorage.getItem(KEY_REFRESH_TOKEN));
    sessionStorage.setItem(KEY_ACCESS_TOKEN, getToken?.data?.accessToken?.token);
    sessionStorage.setItem(KEY_REFRESH_TOKEN, getToken?.data?.refreshToken?.token);
    window.location.reload();
  };

  return (
    <AuthContext.Provider
      value={{
        state,
        dispatch,
      }}
    >
      <KenDialog open={openRequest} onClose={handleOpenRequest} hidePositiveButton={true}>
        <Box component={'div'} padding={'12px'}>
          <Stack direction={'column'} gap={'32px'} alignItems={'start'}>
            <Stack direction={'column'} gap={'12px'} alignItems={'start'}>
              <Typography className={classes.thankyouText}>Session Active on Another Device</Typography>
              <Typography className={classes.subTextModal}>
                Your account has been logged in from another browser or device. To continue here, your session will be refreshed. Unsaved changes may be lost.
              </Typography>
            </Stack>
            <Stack direction={'row'} gap={'12px'} alignItems={'center'} justifyContent={'end'} width={'100%'}>
              <KenButton
                variant="outlined"
                buttonClass={classes.goBack}
                onClick={() => {
                  handleLogout();
                }}
              >
                Logout
              </KenButton>
              <KenButton
                onClick={async () => {
                  await handleRefresh();
                }}
                buttonClass={classes.payBtn}
              >
                Refresh Now
              </KenButton>
            </Stack>
          </Stack>
        </Box>
      </KenDialog>
      {state.loading ? props.children : <KenLoader />}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};
